import { Box, Divider, Spacer } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  batchUpdateBookingAction,
  clearBookingAction,
  editBookingAction,
  editWaitlistAction,
} from '../../data/actions/bookingAction';
import { bookingKeys, bookingPropTypes } from '../../data/models/Booking';
import { paymentSettingsPropTypes } from '../../data/models/PaymentSettings';
import { restaurantPropTypes } from '../../data/models/Restaurant';
import { obeeBookingPropTypes } from '../../data/models/obeeBooking';
import { getVouchersWithTotals } from '../../utils/bookingHelpers';
import { isObeeLogic } from '../../utils/constants';
import { formatCurrency, isEmpty, safeDivision } from '../../utils/helpers';
import { safeParse } from '../../utils/stringHelpers';
import { Typography } from '../Typography';
import useStyles from './PaymentSummaryStyles';

const PaymentSummary = ({ booking, restaurant, obeeBooking, paymentSettings }) => {
  const classes = useStyles();
  const theme = useTheme();

  const getExperienceSummary = () => {
    const experiences = booking?.selectedExperiences;

    if (isEmpty(experiences)) {
      return [];
    }

    return experiences
      .filter((experience) => experience?.count > 0)
      .map((experience) => ({
        name: experience?.experience?.name,
        displayName: experience?.experience?.displayName,
        price: experience?.experience?.price,
        quantity: experience?.count,
        total: experience?.experience?.price * experience?.count,
      }));
  };

  const experienceSummary = getExperienceSummary();

  const hadPayment = isObeeLogic
    ? (obeeBooking?.data?.payments ?? []).some(
        (payment) => payment?.type === 'charges' && payment.success,
      )
    : !isEmpty(booking?.paymentMethodId);

  // Flatten the addons out of their groups to find the individual items
  // const allAddons = flattenArrayValues(addonGroupsToShow, 'addons');

  const addonCounts = Object.entries(booking.addons)
    .filter(([_addonId, addon]) => addon?.count > 0)
    .map(([_addonId, addon]) => {
      // const addon = findObjectByProperty(allAddons, parseInt(addonId, 10), 'id');

      const total = addon?.addon?.includeInPayment ? addon?.addon.price * addon?.count : 0;
      const fullPrice =
        addon?.addon?.percentageOfFullPrice < 100
          ? safeDivision(1, addon?.addon?.percentageOfFullPrice) * total * 100
          : total;
      const payLaterAmount = addon?.addon?.includeInPayment
        ? fullPrice - total
        : addon?.addon?.price * addon?.count;

      return {
        id: addon?.addon?.id,
        name: addon?.addon?.name,
        displayName: addon?.displayName,
        price: addon?.addon?.price,
        quantity: addon?.count,
        total,
        percentageOfFullPrice: addon?.percentageOfFullPrice, // e.g. 50% off
        fullPrice,
        payLaterAmount,
      };
    });

  const experiencesTotal = experienceSummary.reduce((totalPrice, experience) => {
    return totalPrice + experience?.total;
  }, 0);

  // The total dollar value of all vouchers combined. May be more than the purchase total
  const vouchersTotal = booking?.[bookingKeys.voucherReferences].reduce(
    (totalPrice, voucherReference) => {
      return totalPrice + voucherReference?.amount;
    },
    0,
  );

  // Calculate the total they have paid today
  const total =
    addonCounts.reduce((totalPrice, addonCount) => {
      return totalPrice + addonCount?.total;
    }, 0) + (experiencesTotal ?? 0);

  // Subtract the voucher amounts from the total
  const totalIncludingVouchers = Math.max(0, total - vouchersTotal);
  const voucherAmountAfterTotal = Math.max(0, vouchersTotal - total);

  // Calculate the total they will pay later
  const payLaterTotal = addonCounts.reduce((totalPrice, addonCount) => {
    return totalPrice + addonCount?.payLaterAmount;
  }, 0);

  // If there is any voucher money still left, then use that in the pay later amount too
  const payLaterTotalIncludingVouchers = Math.max(
    0,
    restaurant.data.useVouchersOnPayLater ? payLaterTotal - voucherAmountAfterTotal : payLaterTotal,
  );

  if (isEmpty(addonCounts) && isEmpty(booking?.selectedExperiences)) {
    return <></>;
  }

  const maxAmountForVouchers = restaurant.data.useVouchersOnPayLater
    ? total + payLaterTotal
    : total;
  const vouchersWithTotals = getVouchersWithTotals(booking.voucherReferences, maxAmountForVouchers);

  // Calculate transaction fees if applicable
  const transactionFees = useMemo(() => {
    const hasTransactionFees =
      paymentSettings?.data?.transactionFees != null &&
      paymentSettings?.data?.transactionFees?.has_transaction_fee === 1;

    if (!hasTransactionFees) {
      return 0;
    }

    return (
      (obeeBooking?.data?.payments ?? []).reduce((total, payment) => {
        return total + (payment?.fees_cents || 0);
      }, 0) / 100
    );
  }, [obeeBooking?.payments]);

  function generateAmount() {
    if (booking?.ccMode === 'deposit' && isObeeLogic && hadPayment) {
      const totalPaidDeposit = (obeeBooking?.data?.payments ?? [])
        .filter((payment) => payment?.type === 'charges' && payment.success)
        .reduce((totalPaid, payment) => totalPaid + (payment?.amount || 0), 0);
      return formatCurrency(totalPaidDeposit);
    }
    return formatCurrency(totalIncludingVouchers + transactionFees);
  }

  const hideZeroDollarPrice = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
    ?.settingsDetails?.hideZeroDollarPrice;

  const showTotal = () => {
    const totalPrice = generateAmount();
    if (hideZeroDollarPrice === true && totalPrice === '$0.00') {
      return false;
    }
    return true;
  };

  return (
    <>
      <Divider color={theme.colors.dividerColor} />
      <Spacer direction='vertical' gap={12}>
        <Spacer style={{ justifyContent: 'space-between' }}>
          <Typography variant='h4'>Summary</Typography>
          {/* <Button */}
          {/*   type='text' */}
          {/*   variant='text' */}
          {/*   style={{ marginRight: '-16px' }} */}
          {/*   onClick={() => { */}
          {/*     // TODO go to exact addon group */}
          {/*     updateBookingStep(bookingSteps.addons); */}
          {/*   }} */}
          {/* > */}
          {/*   Edit */}
          {/* </Button> */}
        </Spacer>

        <Spacer gap={20} direction='vertical'>
          <table className={classes.summaryTable}>
            {!isEmpty(experienceSummary) &&
              experienceSummary.map((experience) => (
                <tr key={`${experience?.id}-${experience?.name}`}>
                  <td>
                    {experience?.quantity} x {experience?.name}
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontFamily: theme.baseTypography.numberFontFamily,
                    }}
                  >
                    {experience?.total > 0 && formatCurrency(experience?.total)}
                  </td>
                </tr>
              ))}
            {addonCounts.map((addonCount) => (
              <tr key={addonCount?.name}>
                <td>
                  <Spacer gap={2} direction='vertical'>
                    {addonCount?.quantity} x {addonCount?.name}
                    {addonCount?.percentageOfFullPrice < 100 ? (
                      <Box style={{ fontSize: '14px', color: theme.colors.smoke }}>
                        {formatCurrency(addonCount?.fullPrice)} -{' '}
                        {addonCount?.percentageOfFullPrice}% deposit required today
                      </Box>
                    ) : (
                      <>
                        {addonCount?.payLaterAmount > 0 && (
                          <Box style={{ fontSize: '14px', color: theme.colors.smoke }}>
                            {formatCurrency(addonCount?.payLaterAmount)}{' '}
                            {restaurant.data.percentagePaidText}
                          </Box>
                        )}
                      </>
                    )}
                  </Spacer>
                </td>
                <td
                  style={{ textAlign: 'right', fontFamily: theme.baseTypography.numberFontFamily }}
                >
                  {hideZeroDollarPrice === true &&
                  (isEmpty(addonCount?.total) || addonCount?.total === 0)
                    ? null
                    : formatCurrency(addonCount?.total)}
                </td>
              </tr>
            ))}

            {vouchersWithTotals.map((voucherReference) => (
              <tr>
                <td>
                  <Spacer key={voucherReference.reference} direction='vertical' gap={2}>
                    <Box>Voucher reference: {voucherReference?.voucher}</Box>
                    {voucherReference?.remainingAmount > 0 && (
                      <Box style={{ fontSize: '14px', color: theme.colors.smoke }}>
                        {formatCurrency(voucherReference?.remainingAmount)} remaining
                      </Box>
                    )}
                  </Spacer>
                </td>
                <td>
                  <Typography
                    style={{
                      textAlign: 'right',
                      fontFamily: theme.baseTypography.numberFontFamily,
                    }}
                  >
                    -{formatCurrency(voucherReference?.usedAmount)}
                  </Typography>
                </td>
              </tr>
            ))}
          </table>

          {booking.ccMode === 'deposit' && total > 0 && (
            <Spacer
              style={{
                justifyContent: 'space-between',
                fontSize: '18px',
              }}
            >
              <Typography>Total order</Typography>
              <Typography
                style={{
                  fontFamily: theme.baseTypography.numberFontFamily,
                }}
              >
                {formatCurrency(total)}
              </Typography>
            </Spacer>
          )}

          <Spacer direction='vertical' gap={12}>
            {showTotal() && (
              <Spacer
                style={{
                  justifyContent: 'space-between',
                  fontSize: '18px',
                }}
              >
                {/* TODO this assumes a full payment, not a deposit... */}
                <Typography>{hadPayment ? 'Total paid' : 'Total'}</Typography>
                <Typography
                  style={{
                    fontFamily: theme.baseTypography.numberFontFamily,
                  }}
                >
                  {generateAmount()}
                </Typography>
              </Spacer>
            )}

            {transactionFees > 0 && (
              <Spacer
                style={{
                  justifyContent: 'space-between',
                  fontSize: '15px',
                }}
              >
                <Typography>
                  {restaurant.data?.transactionFeesLabel ?? 'Transaction fees'}
                </Typography>
                <Typography
                  style={{
                    fontFamily: theme.baseTypography.numberFontFamily,
                  }}
                >
                  ( {formatCurrency(transactionFees)})
                </Typography>
              </Spacer>
            )}

            {payLaterTotalIncludingVouchers > 0 && (
              <Spacer
                style={{
                  justifyContent: 'space-between',
                  fontSize: '18px',
                }}
              >
                <Typography>{restaurant.data.paymentDueLaterText}</Typography>
                <Typography
                  style={{
                    fontFamily: theme.baseTypography.numberFontFamily,
                  }}
                >
                  {formatCurrency(payLaterTotalIncludingVouchers)}
                </Typography>
              </Spacer>
            )}
          </Spacer>
        </Spacer>
      </Spacer>
    </>
  );
};

PaymentSummary.defaultProps = {};

PaymentSummary.propTypes = {
  booking: bookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  obeeBooking: obeeBookingPropTypes.isRequired,
  paymentSettings: paymentSettingsPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  restaurant: state.restaurant,
  obeeBooking: state.obeeBooking,
  paymentSettings: state.paymentSettings,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      batchUpdateBooking: batchUpdateBookingAction,
      clearBooking: clearBookingAction,
      editBooking: editBookingAction,
      editWaitlist: editWaitlistAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(PaymentSummary);
